import { useEffect, useState } from 'react';
import Widget from '..';
import { getBookmarks, getVideoBookmarks } from '../../services/bookmarks';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import { Bookmark, VideoBookmark } from '../../types/Bookmark';
import { Book } from '../../assets/svgs/Book';
import { Videos } from '../../assets/svgs/Videos';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'react-feather';
import { FormattedBookmark, WIDGET_KEYS } from '../../types/Widgets';
import { BOOKMARK_FAKE_DATA } from '../../utils/fakeData';

interface MyBookmarksProps {
  isDemo?: boolean;
  showDelete?: boolean;
}

const MyBookmarks: React.FC<MyBookmarksProps> = ({
  isDemo = false,
  showDelete = false,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const [bookmarks, setBookmarks] = useState<
    {
      id: string;
      title: string;
      type: 'article' | 'video';
      dateAdded: string;
    }[]
  >([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (isDemo) {
      setBookmarks(BOOKMARK_FAKE_DATA);
    } else {
      fetchBookmarks();
    }
  }, [isDemo]);

  const fetchBookmarks = async () => {
    if (currentUser) {
      const bookmarks = await getBookmarks(currentUser.id);
      const videoBookmarks = await getVideoBookmarks(currentUser.id);
      let formattedBookmarks: FormattedBookmark[] = [];
      let formattedVideoBookmarks: FormattedBookmark[] = [];
      if (bookmarks) {
        formattedBookmarks = formatArticleBookmarks(bookmarks);
      }
      if (videoBookmarks) {
        formattedVideoBookmarks = formatVideoBookmarks(videoBookmarks);
      }
      const allBookmarks = [...formattedBookmarks, ...formattedVideoBookmarks];
      allBookmarks.sort((a, b) => {
        return (
          new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()
        );
      });
      setBookmarks(allBookmarks);
    }
  };

  const formatArticleBookmarks = (bookmarks: Bookmark[]) => {
    const formattedBookmarks = bookmarks.map((bookmark) => ({
      id: bookmark.articleId,
      title: bookmark.articleTitle,
      type: 'article' as const,
      dateAdded: bookmark.createdAt || '',
    }));
    return formattedBookmarks;
  };

  const formatVideoBookmarks = (videoBookmarks: VideoBookmark[]) => {
    const formattedVideoBookmarks = videoBookmarks.map((bookmark) => ({
      id: bookmark.videoId,
      title: bookmark.videoTitle,
      type: 'video' as const,
      dateAdded: bookmark.createdAt || '',
    }));
    return formattedVideoBookmarks;
  };

  return (
    <Widget
      widgetKey={WIDGET_KEYS.MY_BOOKMARKS}
      name={'my-bookmarks'}
      title="My Bookmarks"
      showDelete={showDelete}
    >
      <div className="my-bookmarks">
        {bookmarks.length > 0 ? (
          bookmarks.map((bookmark) => (
            <button
              key={bookmark.id}
              className="button button--link"
              onClick={() => {
                if (bookmark.type === 'article') {
                  navigate(`/library/${bookmark.id}`);
                } else {
                  navigate(`/videos/${bookmark.id}`);
                }
              }}
            >
              {bookmark.type === 'article' ? <Book /> : <Videos />}
              <span>
                {bookmark.title}
                <ArrowRight />
              </span>
            </button>
          ))
        ) : (
          <div className="my-bookmarks_empty">No bookmarks yet</div>
        )}
      </div>
    </Widget>
  );
};

export default MyBookmarks;
